// requestQuote.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios, { type AxiosError, type AxiosResponse } from 'axios'
import { dataInstance } from '../../axiosInstance/instance'

export interface QuotePayload {
  customerId: number
  rentalDetails: {
    startDate: string
    endDate: string
    rentalDays: number
    driverRequired: boolean
    driverCost: number
    transportCost: number
    rentalCost: number
  }
  vehicles: Array<{
    vehicleId: number
    count: number
  }>
  totalCost: number
  delivery: {
    pincode: string
    address: string
    contact: string
  }
}

export const requestQuote = createAsyncThunk(
  'rental/requestQuote',
  async (payload: QuotePayload, { rejectWithValue }) =>
    await dataInstance
      .post<AxiosResponse>('customer/create_request_quote', payload)
      .then((response: AxiosResponse<AxiosResponse>) => response.data.data)
      .catch((error: AxiosError) => {
        if (axios.isAxiosError(error) && error.response != null) {
          const e: any = error.response.data
          return rejectWithValue(e.message)
        }
        return rejectWithValue('Unexpected error! try later')
      })
)

export const fetchCustomerQuotes = createAsyncThunk(
  'rental/fetchCustomerQuotes',
  async (customerId: number, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await dataInstance.get(
        `customer/get_customer_request_quotes/${customerId}`
      )
      return response.data.quotes
    } catch (error) {
      const err = error as AxiosError
      if (err.response?.data) {
        return rejectWithValue((err.response.data as any).message)
      }
      return rejectWithValue('Unexpected error! Please try later')
    }
  }
)
interface QuoteState {
  quotes: any[]
  loading: boolean
  error: string | null
}

const initialState: QuoteState = {
  quotes: [],
  loading: false,
  error: null
}

const quoteSlice = createSlice({
  name: 'quotes',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(requestQuote.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(requestQuote.fulfilled, (state) => {
      state.loading = false
    })
    builder.addCase(requestQuote.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload as string
    })
    builder.addCase(fetchCustomerQuotes.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(fetchCustomerQuotes.fulfilled, (state, action) => {
      state.loading = false
      state.quotes = action.payload
    })
    builder.addCase(fetchCustomerQuotes.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload as string
    })
  }
})

export const quoteSelector = (state: any) => state.quotes
export default quoteSlice.reducer
